import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BarChartIcon, EditIcon, GiftIcon, LogOutIcon, UserIcon } from '../components/ui/icons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MyPage from '../components/MyPage';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { userApi } from '../api/user.api';
import { GetUserDashboardSummaryResponse } from '../types/User.api.type';
import { userInitializer } from '../types/User.initializer';
import MyQuestion from '../components/MyQuestion';
import MyParticipation from '../components/MyParticipation';
import { questionApi } from '../api/question.api';
import { GetQuestionListForMyQuestionResponse } from '../types/Question.api.type';
import { questionInitializer } from '../types/Question.initializer';
import RewardTab from '../components/reward/RewardTab';
import AttendanceTab from '../components/reward/AttendanceTab';
import { useLocation } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';

/**
 * 유저 대시보드
 *
 * - 유저 정보 / 작성 설문 / 참여 설문 등의 정보
 *
 * @author hjkim
 * @constructor
 */
const UserDashBoard = () => {
  /**
   * Hooks
   */
  /* 전역으로 저장된 유저정보를 가져오는 hook */
  const { user, logout } = useAuth();

  const location = useLocation();

  /**
   * States
   */
  const [activeTab, setActiveTab] = useState(() => {
    // TabBar에서 출석 탭 클릭 시 state로 전달
    const stateTab = (location.state as { tab?: string })?.tab;
    if (stateTab === 'attendance') return 'attendance';
    return 'profile';
  });
  console.log('activeTab', activeTab);

  /**
   * APIs
   */
  /* 유저 대시보드 요약정보 조회 API */
  const { data: getDashboardSummaryData } = useQuery<GetUserDashboardSummaryResponse>({
    queryKey: ['getDashboardSummary'],
    initialData: userInitializer.INITIAL_USER_DASHBOARD_SUMMARY,
    queryFn: () =>
      userApi
        .getDashboardSummary()
        .then((res) => res.data.results)
        .catch((err) => console.log(err)),
  });
  /* 내 설문 리스트 조회 API */
  const { data: questionListForMyQuestion } = useQuery<GetQuestionListForMyQuestionResponse>({
    queryKey: ['getQuestionListForMyQuestion'],
    enabled: activeTab === 'my-questions',
    initialData: questionInitializer.INITIAL_GET_QUESTION_LIST_FOR_MY_QUESTION,
    queryFn: () => {
      return questionApi
        .getQuestionListForMyQuestion(user!.id)
        .then((res) => {
          return res.data.results;
        })
        .catch((err) => console.log(err));
    },
  });

  /**
   * Handlers
   */
  /* 로그아웃 */
  const handleLogout = () => {
    if (process.env.NODE_ENV === 'development') {
      logout();
    } else {
      userApi
        .userLogout()
        .then(() => { logout(); })
        .catch(() => { logout(); });
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* 헤더 부분 */}
        <div className="mb-8">
          {/* 사용자 프로필 헤더 */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary rounded-2xl flex items-center justify-center shadow-lgg">
                    <UserIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs">✓</span>
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">대시보드</h1>
                  <p className="text-muted-foreground">나만의 설문을 관리하고 확인해보세요.</p>
                </div>
              </div>
              {/* 로그아웃 버튼 */}
              <div className="flex items-center">
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all duration-200"
                  onClick={handleLogout}>
                  <LogOutIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">로그아웃</span>
                </Button>
              </div>
            </div>
          </div>
          {/* 빠른 통계 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {/* 내가 작성한 설문 CARD */}
            <Card className="group relative overflow-hidden bg-white border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer pt-4">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <EditIcon className="w-6 h-6 text-white" />
                  </div>
                  <Badge
                    variant="outline"
                    className="text-xs bg-blue-50 border-blue-200 text-blue-700 group-hover:bg-blue-100">
                    보기
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {getDashboardSummaryData.totalCreatedSurveyCount}
                </div>
                <div className="text-sm text-gray-600">내가 작성한 설문</div>
              </CardContent>
            </Card>
            {/* 내가 참여한 설문 CARD */}
            <Card className="group relative overflow-hidden bg-white border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer pt-4">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <BarChartIcon className="w-6 h-6 text-white" />
                  </div>
                  <Badge
                    variant="outline"
                    className="text-xs bg-green-50 border-green-200 text-blue-700 group-hover:bg-blue-100">
                    보기
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {getDashboardSummaryData.totalParticipatedSurveyCount}
                </div>
                <div className="text-sm text-gray-600">내가 참여한 설문</div>
              </CardContent>
            </Card>
            {/* 총 투표 수 CARD */}
            <Card className="group relative overflow-hidden bg-white border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer pt-4">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <EditIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-purple-600 mb-1">1523</div>
                <div className="text-sm text-gray-600">받은 좋아요 수</div>
              </CardContent>
            </Card>
            {/* 작성한 댓글 CARD */}
            <Card className="group relative overflow-hidden bg-white border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer pt-4">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <EditIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  {getDashboardSummaryData.totalCommentCount}
                </div>
                <div className="text-sm text-gray-600">작성한 댓글</div>
              </CardContent>
            </Card>
          </div>
          {/* 안내 메시지 */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-lg">💡</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">설문 활동 확인하기</h3>
                <p className="text-sm text-blue-700 leading-relaxed">
                  아래 탭에서
                  <span className="font-semibold bg-blue-100 px-2 py-0.5 rounded">내 설문</span>과
                  <span className="font-semibold bg-blue-100 px-2 py-0.6 rounded ml-1">참여한 설문</span>을 확인하거나,
                  위의 통계 카드를 클릭해보세요!
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* 탭 컨텐츠 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-gray-100 bg-gray-50/50">
              <TabsList className="grid w-full grid-cols-5 h-14 bg-transparent p-0">
                <TabsTrigger
                  value="profile"
                  className="flex items-center justify-center gap-2 h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <UserIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">내 정보</span>
                  <span className="sm:hidden">정보</span>
                </TabsTrigger>
                <TabsTrigger
                  value="my-questions"
                  className="flex items-center justify-center gap-2 h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <EditIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">내 설문</span>
                  <span className="sm:hidden">작성</span>
                  <Badge variant="secondary" className="text-xs ml-1 bg-blue-100 text-blue-700">
                    {getDashboardSummaryData.totalCreatedSurveyCount}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="participations"
                  className="flex items-center justify-center gap-2 h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <BarChartIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">참여한 설문</span>
                  <span className="sm:hidden">참여</span>
                  <Badge variant="secondary" className="text-xs ml-1 bg-green-100 text-green-700">
                    {getDashboardSummaryData.totalParticipatedSurveyCount}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="attendance"
                  className="flex items-center justify-center gap-2 h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <CalendarDays className="w-4 h-4" />
                  <span className="hidden sm:inline">출석</span>
                  <span className="sm:hidden">출석</span>
                </TabsTrigger>
                <TabsTrigger
                  value="rewards"
                  className="flex items-center justify-center gap-2 h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <GiftIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">리워드</span>
                  <span className="sm:hidden">리워드</span>
                </TabsTrigger>
              </TabsList>
            </div>
            <div className="p-6">
              <TabsContent value="profile" className="space-y-6 m-0">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <UserIcon className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-purple-900 mb-1">👤 내 정보 관리</h3>
                      <p className="text-sm text-purple-700 leading-relaxed">
                        프로필 정보를 수정하고 개인 설정을 관리할 수 있습니다.
                      </p>
                    </div>
                  </div>
                </div>
                {/*  마이 페이지 */}
                {user && <MyPage user={user} summary={getDashboardSummaryData} />}
              </TabsContent>
              <TabsContent value="my-questions" className="space-y-6 m-0">
                <div className="bg-gradient-to-r from-blue-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <EditIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 mb-1">📝 내가 작성한 설문</h3>
                      <p className="text-sm text-blue-700 leading-relaxed">
                        여기에서 내가 작성한 모든 설문을 확인하고 관리할 수 있습니다. 진행 중인 설문은 수정하거나 삭제할
                        수 없습니다.
                      </p>
                    </div>
                  </div>
                </div>
                {/*  설문 페이지 */}
                <MyQuestion pagingQuestion={questionListForMyQuestion} />
              </TabsContent>
              <TabsContent value="participations" className="space-y-6 m-0">
                <div className="bg-gradient-to-r from-green-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BarChartIcon className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-900 mb-1">🗳️ 내가 참여한 설문</h3>
                      <p className="text-sm text-green-700 leading-relaxed">
                        여기에서 내가 투표, 댓글, 반응을 남긴 모든 설문을 확인할 수 있습니다. 참여 유형별로 필터링도
                        가능해요.
                      </p>
                    </div>
                  </div>
                </div>
                {/*  설문 참여 페이지 */}
                <MyParticipation />
              </TabsContent>
              <TabsContent value="attendance" className="space-y-6 m-0">
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-teal-900 mb-1">출석 체크</h3>
                      <p className="text-sm text-teal-700 leading-relaxed">
                        매일 출석하면 조각을 받을 수 있어요. 연속 출석하면 보너스 조각도 받을 수 있어요.
                      </p>
                    </div>
                  </div>
                </div>
                <AttendanceTab />
              </TabsContent>
              <TabsContent value="rewards" className="space-y-6 m-0">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <GiftIcon className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-900 mb-1">리워드</h3>
                      <p className="text-sm text-amber-700 leading-relaxed">
                        투표에 참여하면 조각을 모을 수 있어요. 모은 조각은 토스 앱에서 토스 포인트로 바꿀 수 있어요.
                      </p>
                    </div>
                  </div>
                </div>
                <RewardTab />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default UserDashBoard;
